// @flow
import React from 'react'
import classnames from 'classnames'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import './style.css'

type Props = {
  columns: Array<Object>,
  data: Array<Array>,
  width?: string,
  className?: string,
  scrollX?: boolean,
  isWorking?: boolean,
  isDraggable? : boolean,
  onDragStart? : Function,
  onDragUpdate? : Function,
  onDragEnd? : Function,
  droppableType? : string
};

type Column = {
  width?: string,
  label: string,
  direction?: string,
  sortable?: boolean,
  sorting?: boolean,
  onClick?: Function,
  value?: string
};

type Cell = {
  content: any,
  className?: string,
  customKey?: any
};

const getSizeStyle = width => ({width: width})
const onUpdateSort = (callback, value, direction) => {
  callback(value, direction)
}

const TableHeader = columns => (
  <thead>
  <tr>
    {columns.map(({
      label,
      value = '',
      width='auto',
      onClick = ()=>{},
      sortable=false,
      sorting=false,
      direction='desc'
    }:Column, index) => {
      return (
        <th
          className={classnames(`tide-table-column-header--sorting-${direction}`,{
            'tide-table-column-header--sortable': sortable,
            'tide-table-column-header--sorting': sortable && sorting
          })}
          key={`header-cell-${index}`}
          style={getSizeStyle(width)}
          onClick={() => onUpdateSort(onClick, value, direction)}
        >
          <span>{label}</span>
        </th>
      )
    })}
  </tr>
  </thead>
)

const TableRow = (data, columns, isDragging) => (
  data.map(({ content, customKey, className = '' }:Cell, index) => {
    return (
      <td style={isDragging ? getSizeStyle(columns[index]).width : null} key={`cell-${customKey || index}`} className={className}>
        <div style={isDragging ? getSizeStyle(columns[index]).width : null}>
          {content}
        </div>
      </td>
    )
  })
)

const DraggableTableBody = ({ data, columns, onDragStart, onDragUpdate, onDragEnd, droppableType, draggableObjects }) => (
  <DragDropContext
    onDragStart={onDragStart}
    onDragUpdate={onDragUpdate}
    onDragEnd={onDragEnd}
  >
    <Droppable droppableId="__TIDE_TABLE" type={droppableType}>
      {(provided, snapshot) => (
        <tbody
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
        {data.map((obj, index) => {
          return (
            <Draggable key={`row-${draggableObjects[index].id}`} draggableId={`${draggableObjects[index].id}`} index={index}>
              {(provided, snapshot) => {
                return (
                  <tr
                    ref={provided.innerRef}
                    {...provided.dragHandleProps}
                    {...provided.draggableProps}
                    className={
                      classnames(`${obj.className}`, {
                      'tr-is-dragging': snapshot.isDragging
                    })}
                  >
                    {TableRow(obj.data, columns, snapshot.isDragging)}
                  </tr>
              )}}
            </Draggable>
          )}
        )}
        {provided.placeholder}
        </tbody>
      )}
    </Droppable>
  </DragDropContext>
)

const TableBody = (data, columns) => (
  <tbody>
  {data.map(({ data, customKey, className = '' }, index) =>
    <tr key={`row-${customKey || index}`} className={className}>
      {TableRow(data, columns)}
    </tr>
  )}
  </tbody>
)

const DnDTable = ({ columns, data, className='', width='100%', scrollX = false, isWorking = false, isDraggable = false, onDragStart, onDragUpdate, onDragEnd, droppableType = "DEFAULT", draggableObjects = []}:Props) => (
  <div className={classnames('tide-table-wrapper', {
    [className]: !!className,
  })}>
    <table
      style={getSizeStyle(width)}
      className={classnames('tide-table', {
        'tide-table-scroll-x': scrollX
      })}
    >
      {TableHeader(columns)}
      {isDraggable ? DraggableTableBody({ data, columns, onDragStart, onDragUpdate, onDragEnd, droppableType, draggableObjects }) : TableBody(data, columns)}
    </table>
  </div>
)

export default DnDTable
